/**
 * Local API contract between the graphics app (server) and the control app (client).
 * SPEC §3 "Local API", §11.
 *
 * This file is the single source of truth: the zod schemas validate at runtime and
 * the TypeScript types are inferred from them. `docs/local-api.md` is the prose
 * reference.
 *
 * ## Transport
 *
 * - Served by the graphics app on `http://127.0.0.1:4700`, bound to localhost only.
 * - `POST /api/commands`: body is a {@link CommandEnvelope}, response is a {@link CommandResult}
 *   (or an {@link ApiProblem} when the request can't be read as a command at all).
 * - `GET /api/state` (WebSocket upgrade): the server pushes a full {@link StateSnapshotMessage}
 *   after authentication, then {@link StatePatchMessage}s, and a {@link HeartbeatMessage}
 *   every {@link HEARTBEAT_INTERVAL_MS}.
 *
 * ## Authentication
 *
 * A token from config is required on every request.
 * - HTTP: `Authorization: Bearer <token>`.
 * - WebSocket: browsers cannot set headers on a WebSocket, and a token in the URL ends up
 *   in logs, so the client's first frame must be an {@link AuthMessage}. Without a valid one
 *   within {@link WS_AUTH_TIMEOUT_MS} the server closes with {@link WsCloseCode.Unauthorized}.
 *
 * ## Versioning
 *
 * {@link PROTOCOL_VERSION} is sent in the `X-Ioda-Protocol` header and in the WebSocket
 * {@link AuthMessage}. Both apps ship from this repo, so a mismatch is rejected outright
 * (HTTP 426 / close {@link WsCloseCode.ProtocolVersionMismatch}) rather than negotiated.
 * State schemas are strict, so a new server message type or a new state field bumps the
 * version (a strict client rejects keys it doesn't know). A new command type bumps
 * nothing; removing or changing the meaning of anything bumps the version.
 *
 * Apart from the protocol, both apps carry the suite version and their git commit
 * ({@link AppBuild}): the client declares its build in the {@link AuthMessage}, the server
 * publishes both in {@link ApiState.versions}, and a protocol mismatch closes with
 * {@link protocolMismatchReason} so the client can say which graphics version refused it.
 *
 * ## Idempotency
 *
 * `commandId` is an idempotency key. Re-sending an envelope with a known `commandId`
 * returns the stored result without re-executing; re-sending it with a different
 * payload is rejected with `conflict`. A client retrying after a timeout therefore
 * never applies a command twice.
 */
import { z } from 'zod';
import {
  CameraIdSchema,
  CameraSceneKeySchema,
  SceneKeySchema,
} from './fixture.ts';
import { DensitySchema } from './bundle.ts';
import { PaletteColorsSchema, PaletteRoleIdSchema, RendererSchema } from './palette.ts';
import { MAX_PARTICIPANTS, ParticipantSchema, SessionSchema } from './session.ts';
import { SessionFilePersonSchema } from './sessionFile.ts';
import { safeParseWith, type ParseResult } from './validate.ts';
import { AppBuildSchema } from './version.ts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Version of this contract. Both apps must send the same value.
 * - 1: Phase 2/3.
 * - 2: the state stream adds {@link HeartbeatMessage} (SPEC §17). A v1 client would close
 *   on the unknown message and reconnect forever; v2 makes it a clean mismatch instead.
 * - 3: palette and density (ADR 0002): `setPalette`, `resetPalette`, `setDensity`,
 *   `graphics.density` and the palette fields of `admin.bundle`. State schemas are strict,
 *   so a v2 client would reject the new keys on every snapshot.
 * - 4: session files (ADR 0004): `editSession`, and `source` and `files` in `admin.sheet`.
 * - 5: app builds: `client` in {@link AuthMessage}, {@link ApiState.versions}.
 */
export const PROTOCOL_VERSION = 5;

/** Default local API port (overridable in config). */
export const DEFAULT_LOCAL_API_PORT = 4700;

/** HTTP and WebSocket paths served by the graphics app. */
export const API_PATHS = {
  /** `POST`: submit a {@link CommandEnvelope}. */
  commands: '/api/commands',
  /** WebSocket: state snapshots and patches. */
  state: '/api/state',
} as const;

/** HTTP header carrying {@link PROTOCOL_VERSION}. */
export const PROTOCOL_HEADER = 'X-Ioda-Protocol';

/** Largest accepted command body. Larger bodies get HTTP 413. */
export const MAX_COMMAND_BODY_BYTES = 64 * 1024;

/**
 * Shortest accepted API token. The state socket's auth frame isn't rate-limited like
 * HTTP, so the token must be too long to guess. Both apps' config loaders enforce it.
 */
export const MIN_API_TOKEN_LENGTH = 24;

/** How often the state stream sends a {@link HeartbeatMessage}. */
export const HEARTBEAT_INTERVAL_MS = 5_000;

/**
 * A client that hears nothing (no snapshot, patch or heartbeat) for this long treats the
 * graphics app as frozen: it closes the socket and reconnects. Three missed heartbeats.
 */
export const HEARTBEAT_TIMEOUT_MS = 3 * HEARTBEAT_INTERVAL_MS;

/** Time a WebSocket client has to send its {@link AuthMessage}. */
export const WS_AUTH_TIMEOUT_MS = 2_000;

/** WebSocket close codes used by `/api/state` (4000–4999 is the application range). */
export const WsCloseCode = {
  /** Missing, invalid or late {@link AuthMessage}. */
  Unauthorized: 4401,
  /** The client sent something that is not a valid {@link StateClientMessage}. */
  InvalidMessage: 4400,
  /** {@link AuthMessage.protocolVersion} differs from the server's {@link PROTOCOL_VERSION}. */
  ProtocolVersionMismatch: 4426,
  /** The graphics app is shutting down. Clients reconnect with backoff. */
  ServerShutdown: 4503,
} as const;

// ---------------------------------------------------------------------------
// Shared field schemas
// ---------------------------------------------------------------------------

const idString = z.string().trim().min(1).max(128);
/** RFC 3339 timestamp with offset, e.g. `2026-10-04T14:03:00.000Z`. */
const timestamp = z.iso.datetime({ offset: true });

/**
 * Overlay graphics with an on/off toggle on URL3 (SPEC §9.1, §10).
 * The lower third has its own commands because it carries a participant.
 */
export const OverlayToggleSchema = z.enum(['sponsorBox', 'logoBug', 'timer']);
/** An overlay graphic with an on/off toggle. */
export type OverlayToggle = z.infer<typeof OverlayToggleSchema>;

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

/**
 * Payload schemas for every command type, keyed by `type`.
 *
 * Writes go to the graphics app, which owns camera-slot items, CoreVideo assignments
 * and all browser-source content (SPEC §3). Scene switching, audio and outputs are
 * not here: the control app writes those to OBS directly.
 */
export const CommandPayloadSchemas = {
  /**
   * Make a prepared session the current one (drives session cards, nameplates and titles).
   * Rejected with `notFound` for an unknown or cancelled session.
   */
  selectSession: z.strictObject({ sessionId: idString }),

  /**
   * Assign a live CoreVideo participant to a camera input (SPEC §6 "Matching and assignment").
   * Sets the nameplated participant and routes the feed (`assign_output`) together. The
   * graphics app saves the CoreVideo display name as an alias of `participantId`.
   * Returns `accepted` and queues a Pending change if the camera is on program (rule D).
   */
  assignParticipant: z.strictObject({
    cameraId: CameraIdSchema,
    /** Sheet participant (stable ID generated by the graphics app). */
    participantId: idString,
    /** CoreVideo's numeric participant ID from `list_participants`. Never the display name. */
    coreVideoParticipantId: z.number().int().min(0),
  }),

  /**
   * Choose which camera shows in the `Speaker + Screenshare` speaker slot (SPEC §5.5).
   * The graphics app enables that item, disables the others and moves the nameplate.
   */
  setSpeakerShareSpeaker: z.strictObject({ cameraId: CameraIdSchema }),

  /**
   * Re-apply the current layout to a camera scene. Idempotent: this is the recovery
   * for a `partial` result. Queued as Pending if the scene is on program.
   */
  applyLayout: z.strictObject({ sceneKey: CameraSceneKeySchema }),

  /** Apply queued Pending changes: all of them, or one by ID. */
  applyPending: z.discriminatedUnion('target', [
    z.strictObject({ target: z.literal('all') }),
    z.strictObject({ target: z.literal('one'), pendingId: idString }),
  ]),

  /** Drop one queued Pending change without applying it. */
  discardPending: z.strictObject({ pendingId: idString }),

  /** Show or hide an overlay graphic. */
  setGraphicsToggle: z.strictObject({ toggle: OverlayToggleSchema, visible: z.boolean() }),

  /**
   * Show a participant's lower third in the single global slot, on any scene
   * (SPEC §9.4). Replaces a lower third that is already showing.
   */
  showLowerThird: z.strictObject({ participantId: idString }),

  /** Clear the lower third. A no-op (still `completed`) when none is showing. */
  hideLowerThird: z.strictObject({}),

  /** Emergency: hide the lower third and every overlay toggle at once (SPEC §9.4). */
  clearGraphics: z.strictObject({}),

  /**
   * Density of the procedural environment on `signal` bundles (ADR 0002). Goes live at
   * once; surfaces ramp to it over the bundle's `density.rampFrames`.
   */
  setDensity: z.strictObject({ density: DensitySchema }),

  /**
   * One-click CoreVideo feed recovery (SPEC §7): `stale` runs `recover_stale_outputs`,
   * `lowQuality` runs `upgrade_low_quality_outputs`.
   */
  recoverFeeds: z.strictObject({ mode: z.enum(['stale', 'lowQuality']) }),

  // --- Admin commands (graphics admin UI: Prepare, Bind fixture, Preflight) ---------

  /** Fetch the sheet and stage the result with a diff; nothing changes until `applySheetRefresh`. */
  refreshSheet: z.strictObject({}),

  /**
   * Apply the staged refresh or edit: on-air text updates live, with transitions (SPEC §6,
   * §9.4). A staged `editSession` first backs up and rewrites the session file; if that write
   * fails nothing is applied.
   */
  applySheetRefresh: z.strictObject({}),

  /**
   * Edit one session's people and text in its session file (ADR 0004). Staged like a refresh:
   * the diff shows in `admin.sheet.staged` and nothing changes until `applySheetRefresh`.
   * Each person keeps their `participantId` (`null` for someone new), so a rename or reorder
   * keeps assignments and aliases. Type, status and the session list can't be edited.
   * Rejected with `preconditionFailed` unless the source is `sessionFiles` (the sheet is never
   * written), and with `invalidCommand` for data a session file can't hold.
   */
  editSession: z.strictObject({
    sessionId: idString,
    session: z.strictObject({
      title: z.string().max(2000),
      theme: z.string().max(2000),
      track: z.string().max(2000),
      day: z.string().max(2000),
      startTime: z.string().max(2000),
      endTime: z.string().max(2000),
      /** Empty string removes the field. */
      location: z.string().max(2000),
      /** Empty string removes the field. */
      description: z.string().max(2000),
    }),
    host: SessionFilePersonSchema.extend({ participantId: idString.nullable() }).nullable(),
    /** In order: the first is CAM-2. */
    speakers: z.array(SessionFilePersonSchema.extend({ participantId: idString.nullable() })).max(MAX_PARTICIPANTS),
  }),

  /** Scan OBS and claim the fixture's scene items (SPEC §8.3). Replaces all bindings. */
  bindFixture: z.strictObject({}),

  /** Run every preflight check now (SPEC §12). */
  runPreflight: z.strictObject({}),

  /** Proceed despite a preflight *warning* (never a block). The override is logged. */
  overridePreflightWarning: z.strictObject({ checkId: z.string().min(1).max(100), note: z.string().max(500).nullable() }),

  /** Import a layout file from the builder into the project's layouts directory. */
  importLayout: z.strictObject({
    fileName: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*\.json$/, 'must be a kebab-case .json file name'),
    layout: z.unknown(),
  }),

  /** Pick the graphics bundle (SPEC §9.3); `null` returns to the config's default. */
  selectBundle: z.strictObject({ bundleId: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/).nullable() }),

  /**
   * Set a bundle's palette override (Prepare only, ADR 0002): `colors` replaces the whole
   * override; roles left out use the bundle default. Goes live on save. Rejected with
   * `paletteLocked` while OBS is streaming, recording or disconnected, and with
   * `invalidCommand` for a role the bundle does not declare.
   */
  setPalette: z.strictObject({ bundleId: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/), colors: PaletteColorsSchema }),

  /** Drop a bundle's palette override (back to its default). Same lock as `setPalette`. */
  resetPalette: z.strictObject({ bundleId: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/) }),

  /** Have CoreVideo join a Zoom meeting. The passcode is redacted from every log. */
  joinMeeting: z.strictObject({ meetingId: z.string().trim().min(1).max(64), passcode: z.string().max(128), displayName: z.string().trim().min(1).max(100) }),

  /** Have CoreVideo leave the meeting. */
  leaveMeeting: z.strictObject({}),

  /**
   * Start CoreVideo's raw media (its "Start Engine" button): joining does not start it.
   * After a `joinMeeting` the graphics app sends it once by itself; this command covers a
   * join made in CoreVideo's own dock, or a retry. Fails while not in a meeting.
   */
  startMedia: z.strictObject({}),
} as const;

/** Every command `type`. */
export type CommandType = keyof typeof CommandPayloadSchemas;

/** Every command `type`, as a runtime list. */
export const COMMAND_TYPES = Object.keys(CommandPayloadSchemas) as CommandType[];

/** The payload type for command `T`. */
export type CommandPayload<T extends CommandType> = z.infer<(typeof CommandPayloadSchemas)[T]>;

/**
 * A command sent by the control app (SPEC §11).
 * @typeParam T The command type; narrows `payload`.
 */
export interface CommandEnvelope<T extends CommandType = CommandType> {
  /** Client-generated UUID. Correlation ID in the action log and idempotency key. */
  commandId: string;
  /** Which command. */
  type: T;
  /** Command-specific payload, see {@link CommandPayloadSchemas}. */
  payload: CommandPayload<T>;
  /** When the client issued it (RFC 3339). Diagnostics only; the server never trusts it for ordering. */
  issuedAt: string;
}

const envelopeFor = <T extends CommandType>(type: T) =>
  z.strictObject({
    commandId: z.uuid(),
    type: z.literal(type),
    payload: CommandPayloadSchemas[type],
    issuedAt: timestamp,
  });

/** Validates any {@link CommandEnvelope}. Discriminated on `type`; extra keys are rejected. */
export const CommandEnvelopeSchema = z.discriminatedUnion(
  'type',
  COMMAND_TYPES.map(envelopeFor) as unknown as [
    ReturnType<typeof envelopeFor<CommandType>>,
    ...ReturnType<typeof envelopeFor<CommandType>>[],
  ],
);

/** A validated command of any type, discriminated on `type`. */
export type AnyCommandEnvelope = { [T in CommandType]: CommandEnvelope<T> }[CommandType];

/**
 * Validates an incoming command body.
 * @returns The typed envelope, or every validation issue.
 */
export function parseCommandEnvelope(data: unknown): ParseResult<AnyCommandEnvelope> {
  return safeParseWith(CommandEnvelopeSchema, data) as ParseResult<AnyCommandEnvelope>;
}

/**
 * Builds an envelope with a fresh `commandId` and `issuedAt`.
 * @param newId UUID generator, `crypto.randomUUID` in both apps.
 */
export function createCommand<T extends CommandType>(
  type: T,
  payload: CommandPayload<T>,
  newId: () => string,
  now: () => Date = () => new Date(),
): CommandEnvelope<T> {
  return { commandId: newId(), type, payload, issuedAt: now().toISOString() };
}

// ---------------------------------------------------------------------------
// Results and errors
// ---------------------------------------------------------------------------

/**
 * Outcome of a command.
 * - `accepted`: valid and recorded, but not applied yet (queued as Pending because the
 *   target is on program). Watch `pending` in the state stream.
 * - `completed`: applied and acknowledged/reconciled. Never reported before that (SPEC §13).
 * - `rejected`: not executed (validation, unknown ID, precondition). Nothing changed.
 * - `partial`: a multi-step operation stopped part-way. Re-run it; operations are idempotent.
 * - `failed`: execution was attempted and failed (e.g. OBS or CoreVideo error). See `error.recoverable`.
 */
export const CommandStatusSchema = z.enum(['accepted', 'completed', 'rejected', 'partial', 'failed']);
/** Outcome of a command; see {@link CommandStatusSchema}. */
export type CommandStatus = z.infer<typeof CommandStatusSchema>;

/**
 * Machine-readable error codes in {@link CommandResult.error}.
 * - `invalidCommand`: envelope or payload failed validation. `message` lists every issue.
 * - `notFound`: a referenced session, participant, camera or pending change does not exist.
 * - `conflict`: `commandId` was already used with a different payload.
 * - `preconditionFailed`: the current state forbids it (e.g. session not ready, no assignment).
 * - `bindingBroken`: a required OBS scene-item binding is broken; re-run Bind fixture (SPEC §8.3).
 * - `obsUnavailable` / `coreVideoUnavailable` / `sheetUnavailable`: the upstream is disconnected.
 * - `upstreamError`: the upstream answered with an error.
 * - `paletteLocked`: palette edits are refused while OBS is streaming, recording or disconnected (ADR 0002).
 * - `timeout`: no acknowledgement in time; state is unknown until reconciliation.
 * - `internal`: a bug in the graphics app.
 */
export const CommandErrorCodeSchema = z.enum([
  'invalidCommand',
  'notFound',
  'conflict',
  'preconditionFailed',
  'bindingBroken',
  'obsUnavailable',
  'coreVideoUnavailable',
  'sheetUnavailable',
  'upstreamError',
  'paletteLocked',
  'timeout',
  'internal',
]);
/** Machine-readable command error code; see {@link CommandErrorCodeSchema}. */
export type CommandErrorCode = z.infer<typeof CommandErrorCodeSchema>;

/** Error detail on a non-successful {@link CommandResult}. */
export const CommandErrorSchema = z.strictObject({
  code: CommandErrorCodeSchema,
  /** Human-readable and actionable. Never contains credentials. */
  message: z.string().max(4000),
  /** True if retrying (or re-running) can succeed without operator changes. */
  recoverable: z.boolean(),
});
/** Error detail on a non-successful {@link CommandResult}. */
export type CommandError = z.infer<typeof CommandErrorSchema>;

/**
 * The server's answer to a {@link CommandEnvelope} (SPEC §11). Always returned for a
 * request whose `commandId` could be read, whatever the status.
 */
export const CommandResultSchema = z
  .strictObject({
    /** Echo of {@link CommandEnvelope.commandId}. */
    commandId: z.string().min(1),
    status: CommandStatusSchema,
    /** Intent revision after the command, when it changed intent. */
    resultingRevision: z.number().int().min(0).optional(),
    /** Present exactly when `status` is `rejected`, `partial` or `failed`. */
    error: CommandErrorSchema.optional(),
  })
  .refine((r) => (r.error !== undefined) === ['rejected', 'partial', 'failed'].includes(r.status), {
    message: 'error must be present exactly when status is rejected, partial or failed',
    path: ['error'],
  });
/** The server's answer to a command. */
export type CommandResult = z.infer<typeof CommandResultSchema>;

/**
 * HTTP status for a {@link CommandResult}. Clients should branch on the body's `status`;
 * the HTTP code exists for logs and tooling.
 */
export function httpStatusForResult(status: CommandStatus): number {
  switch (status) {
    case 'completed':
    case 'partial':
      return 200;
    case 'accepted':
      return 202;
    case 'rejected':
      return 422;
    case 'failed':
      return 500;
  }
}

/**
 * Transport-level error codes, for requests that cannot be read as a command at all
 * (so there is no `commandId` to answer with a {@link CommandResult}).
 */
export const ApiProblemCodeSchema = z.enum([
  /** Body is not JSON, or has no readable `commandId`. HTTP 400. */
  'malformedRequest',
  /** Missing or wrong bearer token. HTTP 401. */
  'unauthorized',
  /** `Host` is not `127.0.0.1:<port>` / `localhost:<port>` (DNS-rebinding guard). HTTP 403. */
  'forbiddenHost',
  /** Cross-origin request from an origin not in `localApi.allowedOrigins`. HTTP 403. */
  'forbiddenOrigin',
  /** Too many failed authentications from this client; retry after `Retry-After`. HTTP 429. */
  'tooManyRequests',
  /** Wrong path or method. HTTP 404 / 405. */
  'notFound',
  /** Body larger than {@link MAX_COMMAND_BODY_BYTES}. HTTP 413. */
  'payloadTooLarge',
  /** `Content-Type` is not `application/json`. HTTP 415. */
  'unsupportedMediaType',
  /** `X-Ioda-Protocol` missing or different from {@link PROTOCOL_VERSION}. HTTP 426. */
  'protocolVersionMismatch',
]);
/** Transport-level error code; see {@link ApiProblemCodeSchema}. */
export type ApiProblemCode = z.infer<typeof ApiProblemCodeSchema>;

/**
 * RFC 7807 problem details for transport-level errors, served as `application/problem+json`.
 * `type` is `urn:ioda:problem:<code>`.
 */
export const ApiProblemSchema = z.strictObject({
  type: z.string().startsWith('urn:ioda:problem:'),
  title: z.string(),
  status: z.number().int().min(400).max(599),
  detail: z.string(),
  code: ApiProblemCodeSchema,
});
/** RFC 7807 problem details for transport-level errors. */
export type ApiProblem = z.infer<typeof ApiProblemSchema>;

// ---------------------------------------------------------------------------
// State pushed on /api/state
// ---------------------------------------------------------------------------

/** A session with its participants, for the session cards and the picker. */
export const SessionCardSchema = z.strictObject({
  session: SessionSchema,
  /** Host first, then by `order`. Empty for a break. */
  participants: z.array(ParticipantSchema).max(MAX_PARTICIPANTS),
});
/** A session with its participants. */
export type SessionCard = z.infer<typeof SessionCardSchema>;

/** Who is on a camera input. Participant and feed are set together by `assignParticipant`. */
export const CameraAssignmentSchema = z.strictObject({
  cameraId: CameraIdSchema,
  /** Sheet participant who is nameplated, or `null` if unassigned. */
  participantId: idString.nullable(),
  /** CoreVideo participant routed to this input, or `null`. */
  coreVideoParticipantId: z.number().int().min(0).nullable(),
});
/** Who is on a camera input. */
export type CameraAssignment = z.infer<typeof CameraAssignmentSchema>;

/**
 * A change held back because its target is on program (SPEC §5.4 rule D). Applied with
 * `applyPending` after cutting to Starting, Next Session or BRB.
 */
export const PendingChangeSchema = z.strictObject({
  pendingId: idString,
  kind: z.enum(['assignment', 'layout']),
  /** The scene the change re-lays out, if any. */
  sceneKey: SceneKeySchema.nullable(),
  /** The camera the change re-assigns, if any. */
  cameraId: CameraIdSchema.nullable(),
  /** One-line description for the operator, e.g. "CAM-3 → Ana Silva". */
  summary: z.string().max(500),
  createdAt: timestamp,
  /** The command that queued it. */
  commandId: z.string().min(1),
});
/** A change queued because its target is on program. */
export type PendingChange = z.infer<typeof PendingChangeSchema>;

/** What the overlay and lower third show now. */
export const GraphicsStateSchema = z.strictObject({
  toggles: z.strictObject({
    sponsorBox: z.boolean(),
    logoBug: z.boolean(),
    timer: z.boolean(),
  }),
  lowerThird: z.strictObject({
    /** Participant shown, or `null` when the slot is empty. */
    participantId: idString.nullable(),
    /** `auto` when triggered by a Single scene going on program (SPEC §9.4). */
    trigger: z.enum(['auto', 'manual']).nullable(),
    /** When an auto lower third hides itself; `null` for manual or none. */
    hidesAt: timestamp.nullable(),
  }),
  /** Density of the procedural environment (`signal` bundles; ignored by `dom`). */
  density: DensitySchema,
});
/** What the overlay and lower third show now. */
export type GraphicsState = z.infer<typeof GraphicsStateSchema>;

/** CoreVideo meeting states (from its `status` command). */
export const MeetingStateSchema = z.enum(['idle', 'joining', 'in_meeting', 'leaving', 'recovering', 'failed']);
/** CoreVideo meeting state. */
export type MeetingState = z.infer<typeof MeetingStateSchema>;

/** Preflight result levels (SPEC §12). */
export const PreflightLevelSchema = z.enum(['pass', 'warning', 'block', 'unavailable']);
/** Preflight result level. */
export type PreflightLevel = z.infer<typeof PreflightLevelSchema>;

/**
 * Health as observed by the graphics app. OBS telemetry (stats, stream status, meters)
 * is not here: the control app reads it from OBS directly.
 */
export const HealthSchema = z.strictObject({
  obs: z.strictObject({
    connected: z.boolean(),
    /** As last reported by `CurrentProgramSceneChanged`. */
    programSceneName: z.string().nullable(),
    /** Scene-item bindings marked broken (SPEC §8.3). Non-zero blocks layout apply. */
    brokenBindings: z.number().int().min(0),
  }),
  coreVideo: z.strictObject({
    connected: z.boolean(),
    meetingState: MeetingStateSchema.nullable(),
    /** CoreVideo's raw media is running. In a meeting without it, no feed gets frames (`startMedia`). */
    mediaActive: z.boolean(),
    /**
     * Zoom screen share as CoreVideo's share output reports it. When a share ends, OBS keeps
     * showing its last frame frozen, so `Speaker + Screenshare` needs this to be honest.
     * `available` = someone shares, CoreVideo's media runs and frames arrive (OBS shows it);
     * `starting` = someone shares but the first frames haven't arrived yet (3–6 s on the rig;
     * OBS still shows the previous share's frozen frame); `sharerName` is set whenever
     * someone shares, so "sharing but media off" is visible.
     */
    share: z.strictObject({
      available: z.boolean(),
      starting: z.boolean(),
      sharerName: z.string().max(200).nullable(),
      /** The camera assigned to the sharer, if any: Speaker + Screenshare follows it off program, and only marks it on program. */
      sharerCameraId: CameraIdSchema.nullable(),
    }),
    /**
     * Cameras whose CoreVideo output no longer routes the participant this app assigned
     * (changed outside the app, e.g. CoreVideo's Output Manager). `actual: null` = nobody.
     * `expected: 0` = unassigned here, but the output routes someone in the meeting.
     * Shown, never repaired automatically: the operator re-assigns in Prepare.
     */
    assignmentDrift: z.array(z.strictObject({ cameraId: CameraIdSchema, expected: z.number().int().min(0), actual: z.number().int().min(0).nullable() })).max(6),
    /** Feeds stale or below the minimum resolution. */
    unhealthyFeeds: z.number().int().min(0),
  }),
  sheet: z.strictObject({
    /** Time of the last successful fetch. The client shows the age. */
    lastFetchAt: timestamp.nullable(),
    /** Last fetch error, if the latest attempt failed. Never contains credentials. */
    lastError: z.string().max(1000).nullable(),
  }),
  /** One entry per expected graphics surface (SPEC §9.1). */
  surfaces: z
    .array(
      z.strictObject({
        surface: z.enum(['background', 'cameras', 'overlay', 'scene']),
        /** Scene name for `cameras` and `scene` surfaces; `null` otherwise. */
        scene: z.string().nullable(),
        /** Connected and has completed the render-ready handshake. */
        ready: z.boolean(),
      }),
    )
    .max(64),
  /** Overall preflight result for the current session. Never gates the control app (SPEC §12). */
  preflight: PreflightLevelSchema,
});
/** Health as observed by the graphics app. */
export type Health = z.infer<typeof HealthSchema>;

/** One graphics surface and whether it completed the render-ready handshake. */
export type SurfaceInfo = Health['surfaces'][number];

/** One preflight check result (SPEC §12). */
export const PreflightResultSchema = z.strictObject({
  /** Stable ID, e.g. `obs.connected`, `bindings.intact`, `disk.free`. */
  checkId: z.string().min(1).max(100),
  level: PreflightLevelSchema,
  /** Block-type checks can't be overridden; warning-type checks can. */
  kind: z.enum(['block', 'warn']),
  title: z.string().max(200),
  detail: z.string().max(2000),
  /** What to do about it; empty when passing. */
  remediation: z.string().max(1000),
  /** A logged override is in effect (warnings only). */
  overridden: z.boolean(),
});
/** One preflight check result. */
export type PreflightResult = z.infer<typeof PreflightResultSchema>;

/**
 * What the graphics admin UI needs on top of the control app's view (Prepare, Bind
 * fixture, Preflight). The control app may ignore it.
 */
export const AdminStateSchema = z.strictObject({
  /** Live CoreVideo roster, `ignoredParticipantPatterns` excluded. */
  roster: z.array(z.strictObject({ coreVideoParticipantId: z.number().int().min(0), name: z.string().max(200), hasVideo: z.boolean() })).max(300),
  /** Roster entries hidden by `ignoredParticipantPatterns` (e.g. IODA-ZOOM bridge accounts). */
  ignoredRosterCount: z.number().int().min(0),
  /**
   * Per camera, the sheet person expected on it (CAM-1 host, then speaker order) and the
   * certain CoreVideo match to prefill, if any. `candidates` > 1 means uncertain.
   */
  prefill: z.array(
    z.strictObject({
      cameraId: CameraIdSchema,
      participantId: idString.nullable(),
      suggestion: z.number().int().min(0).nullable(),
      reason: z.enum(['alias', 'name']).nullable(),
      candidates: z.array(z.number().int().min(0)).max(50),
    }),
  ).length(6),
  outputs: z.array(
    z.strictObject({
      source: z.string(),
      coreVideoParticipantId: z.number().int().min(0).nullable(),
      width: z.number().min(0),
      height: z.number().min(0),
      healthy: z.boolean(),
      /** CoreVideo's own diagnosis, e.g. `zoom_delivered_lower_resolution`; `null` when it gives none. */
      healthReason: z.string().max(64).nullable(),
    }),
  ).max(32),
  meetingState: MeetingStateSchema.nullable(),
  bindings: z.strictObject({
    boundAt: timestamp.nullable(),
    sceneCollection: z.string().nullable(),
    total: z.number().int().min(0),
    broken: z.array(z.strictObject({ sceneKey: SceneKeySchema, target: z.string(), reason: z.string() })).max(200),
    /** Problems from the last Bind fixture run (missing or ambiguous items). */
    problems: z.array(z.string().max(500)).max(200),
  }),
  sheet: z.strictObject({
    /** Where session data comes from (config `sheets.source.kind`); only `sessionFiles` can be edited. */
    source: z.enum(['sessionFiles', 'google', 'file']),
    /**
     * `sessionFiles` only: every `*.json` in the folder from the last scan, in name order.
     * `error` is why a file was not loaded (its session keeps its last good data). Empty for
     * the other sources.
     */
    files: z.array(z.strictObject({ fileName: z.string().max(260), sessionId: idString.nullable(), error: z.string().max(2000).nullable() })).max(500),
    /** A fetched refresh or an edit waiting for `applySheetRefresh`, with a human-readable diff. */
    staged: z.strictObject({ fetchedAt: timestamp, diff: z.array(z.string().max(500)).max(500), empty: z.boolean() }).nullable(),
    errors: z.array(z.string().max(1000)).max(200),
    warnings: z.array(z.string().max(1000)).max(200),
  }),
  layouts: z.strictObject({
    files: z.array(z.strictObject({ fileName: z.string(), layoutId: z.string().nullable(), slotCount: z.number().int().nullable(), error: z.string().max(2000).nullable() })).max(100),
    /** Layout file last applied per camera scene; what the GFX Cameras surfaces draw. */
    applied: z.record(z.string(), z.string().nullable()),
    /** Layout files the scene map references but the layouts directory lacks. */
    missing: z.array(z.string()).max(50),
  }),
  preflight: z.strictObject({ ranAt: timestamp.nullable(), results: z.array(PreflightResultSchema).max(100) }),
  bundle: z.strictObject({
    active: z.string(),
    available: z.array(z.string()).max(50),
    /** The active bundle's sponsor rotation, by name; empty if it failed to load. */
    sponsors: z.array(z.string().max(100)).max(12),
    /** The active bundle's renderer, or `null` if it failed to load. */
    renderer: RendererSchema.nullable(),
    /** The active bundle's palette for the Prepare editor, or `null` if it failed to load. */
    palette: z
      .strictObject({
        roles: z.array(z.strictObject({ id: PaletteRoleIdSchema, label: z.string() })).max(16),
        defaults: PaletteColorsSchema,
        presets: z.array(z.strictObject({ name: z.string(), colors: PaletteColorsSchema })).max(12),
        /** The project's override for this bundle (empty when none). */
        override: PaletteColorsSchema,
        /** What the surfaces draw with. */
        effective: PaletteColorsSchema,
        /** Override roles the bundle no longer declares (ignored). */
        unknownOverrideRoles: z.array(z.string()).max(32),
        /** Declared pairs below the minimum contrast. */
        contrastWarnings: z.array(z.strictObject({ fg: z.string(), bg: z.string(), ratio: z.number() })).max(16),
      })
      .nullable(),
    /** Why palette edits are refused now (streaming, recording, OBS down), or `null` when allowed. */
    paletteLocked: z.string().max(200).nullable(),
  }),
});
/** The admin UI's extra view of state. */
export type AdminState = z.infer<typeof AdminStateSchema>;

/** Everything the control app needs from the graphics app. */
export const ApiStateSchema = z.strictObject({
  /** Intent revision (SPEC §11). Increments on every intent change. */
  intentRevision: z.number().int().min(0),
  currentSessionId: idString.nullable(),
  /** All non-cancelled sessions in schedule order. */
  sessions: z.array(SessionCardSchema).max(500),
  /** Exactly one entry per camera, CAM-1 … CAM-6. */
  cameras: z.array(CameraAssignmentSchema).length(6),
  /** Camera shown in the Speaker + Screenshare speaker slot. */
  speakerShare: z.strictObject({ cameraId: CameraIdSchema.nullable() }),
  graphics: GraphicsStateSchema,
  pending: z.array(PendingChangeSchema).max(100),
  health: HealthSchema,
  /** Prepare/preflight details for the graphics admin UI. */
  admin: AdminStateSchema,
  /**
   * The graphics app's build, and the build of the control app connected last (`null` until
   * one has connected since the graphics app started). The control app warns when the
   * versions differ (SPEC §17).
   */
  versions: z.strictObject({ graphics: AppBuildSchema, control: AppBuildSchema.nullable() }),
});
/** Everything the control app needs from the graphics app. */
export type ApiState = z.infer<typeof ApiStateSchema>;

/** A top-level key of {@link ApiState}; patches replace whole keys. */
export type ApiStateKey = keyof ApiState;

// ---------------------------------------------------------------------------
// WebSocket messages
// ---------------------------------------------------------------------------

/** Client → server, first frame: authenticate and declare the protocol version. */
export const AuthMessageSchema = z.strictObject({
  type: z.literal('auth'),
  protocolVersion: z.number().int(),
  token: z.string().min(1).max(512),
  /** Which app and build is connecting: logged, and a control client's build is published. */
  client: AppBuildSchema.extend({ app: z.enum(['control', 'admin']) }),
});
/** Client → server, first frame. */
export type AuthMessage = z.infer<typeof AuthMessageSchema>;

/** Client → server: ask for a fresh snapshot (sent after a `seq` gap). */
export const ResyncMessageSchema = z.strictObject({ type: z.literal('resync') });
/** Client → server: ask for a fresh snapshot. */
export type ResyncMessage = z.infer<typeof ResyncMessageSchema>;

/** Any message the client may send. */
export const StateClientMessageSchema = z.discriminatedUnion('type', [AuthMessageSchema, ResyncMessageSchema]);
/** Any message the client may send. */
export type StateClientMessage = z.infer<typeof StateClientMessageSchema>;

/**
 * Server → client: the full state. Sent after authentication and after every `resync`.
 * Resets the client's `seq`.
 */
export const StateSnapshotMessageSchema = z.strictObject({
  type: z.literal('snapshot'),
  /** Stream sequence number. The next patch has `seq + 1`. */
  seq: z.number().int().min(0),
  /**
   * Server clock at send time. Clients derive the clock offset from it so timers
   * (target time + offset) never drift (SPEC §9.4).
   */
  serverTime: timestamp,
  protocolVersion: z.literal(PROTOCOL_VERSION),
  state: ApiStateSchema,
});
/** Server → client: the full state. */
export type StateSnapshotMessage = z.infer<typeof StateSnapshotMessageSchema>;

/**
 * Server → client: replace the listed top-level keys of the state. A patch whose `seq`
 * is not exactly the previous `seq + 1` means messages were lost: the client discards
 * it and sends {@link ResyncMessage}.
 */
export const StatePatchMessageSchema = z.strictObject({
  type: z.literal('patch'),
  seq: z.number().int().min(1),
  serverTime: timestamp,
  /** Whole-key replacements; keys not present are unchanged. At least one key. */
  changes: ApiStateSchema.partial().refine((c) => Object.keys(c).length > 0, {
    message: 'a patch must change at least one key',
  }),
});
/** Server → client: replace some top-level keys. */
export type StatePatchMessage = z.infer<typeof StatePatchMessageSchema>;

/**
 * Server → client, every {@link HEARTBEAT_INTERVAL_MS}: the graphics app is alive. Carries
 * no state and no `seq`. Browsers can't observe WebSocket ping frames, so a frozen server
 * whose socket stays open is only detectable through this message (SPEC §17).
 */
export const HeartbeatMessageSchema = z.strictObject({
  type: z.literal('heartbeat'),
  /** Server clock at send time; refreshes the client's clock offset. */
  serverTime: timestamp,
});
/** Server → client: the graphics app is alive. */
export type HeartbeatMessage = z.infer<typeof HeartbeatMessageSchema>;

/** Any message the server may send. */
export const StateServerMessageSchema = z.discriminatedUnion('type', [
  StateSnapshotMessageSchema,
  StatePatchMessageSchema,
  HeartbeatMessageSchema,
]);
/** Any message the server may send. */
export type StateServerMessage = z.infer<typeof StateServerMessageSchema>;

/**
 * Applies a snapshot or patch to the client's copy of the state. Heartbeats carry no
 * state: handle them before calling this.
 * @returns The new state and `seq`, or `resync` when a snapshot is needed (no snapshot
 *   yet, or a `seq` gap). Pure; never mutates `current`.
 */
export function applyStateMessage(
  current: { state: ApiState; seq: number } | null,
  message: StateSnapshotMessage | StatePatchMessage,
): { state: ApiState; seq: number } | 'resync' {
  if (message.type === 'snapshot') return { state: message.state, seq: message.seq };
  if (current === null || message.seq !== current.seq + 1) return 'resync';
  return { state: { ...current.state, ...message.changes } as ApiState, seq: message.seq };
}
